import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth';
import { PlanExpiredService } from '../../services/plan-expired.service';
import { MyPlanData } from '../../core/models/subscription.model';
import { RoleId } from '../../core/models/role.model';

@Component({
  selector: 'app-plan-expiry-banner',
  templateUrl: './plan-expiry-banner.component.html',
  styleUrls: ['./plan-expiry-banner.component.scss'],
  standalone: true,
  imports: [CommonModule],
})
export class PlanExpiryBannerComponent implements OnInit, OnDestroy {
  private _planExpiredService = inject(PlanExpiredService);
  private _authService        = inject(AuthService);
  private _sub!: Subscription;

  daysRemaining = signal<number | null>(null);
  dismissed     = signal(false);
  planName      = signal<string>('');

  readonly showBanner = computed(() => {
    const days = this.daysRemaining();
    return !this.dismissed() && days !== null && days >= 0 && days <= 5;
  });

  readonly urgencyClass = computed(() => {
    const days = this.daysRemaining();
    if (days === 0) return 'urgency-critical';
    if (days !== null && days <= 2) return 'urgency-high';
    return 'urgency-warning';
  });

  readonly bannerMessage = computed(() => {
    const days = this.daysRemaining();
    const plan = this.planName();
    if (days === 0) {
      return `Tu plan <strong>${plan}</strong> vence <strong>hoy</strong>. No pierdas el acceso a tus campañas y registros.`;
    }
    if (days === 1) {
      return `Tu plan <strong>${plan}</strong> vence <strong>mañana</strong>. Asegura la continuidad de tu trabajo actualizando ahora.`;
    }
    return `Tu plan <strong>${plan}</strong> vence en <strong>${days} días</strong>. Aprovecha el tiempo para migrar a un plan superior sin interrupciones.`;
  });

  ngOnInit(): void {
    const isGestor = this._authService.hasRoleId(RoleId.GESTOR);
    const isEditor = this._authService.hasRoleId(RoleId.EDITOR);
    if (!isGestor && !isEditor) return;

    // Lee el dato ya cargado por el guard — sin llamada extra a la API
    this._sub = this._planExpiredService.planData$.subscribe({
      next: (data: MyPlanData | null) => {
        if (!data?.subscription) return;

        const endsAt = data.subscription.ends_at;
        if (!endsAt) return;

        const planLabel = data.subscription.plan?.name ?? 'actual';
        this.planName.set(planLabel);

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const end = new Date(endsAt);
        end.setHours(0, 0, 0, 0);

        const diffMs   = end.getTime() - today.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays >= 0 && diffDays <= 5) {
          this.daysRemaining.set(diffDays);
        }
      },
    });
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
  }

  dismiss(): void {
    this.dismissed.set(true);
  }

  goToPlans(): void {
    window.open('https://wa.link/l4gjr4', '_blank', 'noopener,noreferrer');
  }
}
